<template>
  <div class="docs">
    <ui-title :title="t('docs.policy.title')" :wave="'/docsTitleBg.png'" />
    <div class="docs__wrapper">
      <docs-navigation
          ref="navRef"
          :list="navigationList"
          @navigate="scrollToSection"
          :active-section="activeSection"
      />
      <el-scrollbar
          ref="scrollbarRef"
          always
          class="docs__main"
          @scroll="handleScroll"
      >
        <div
            v-for="(el, idx) of policy"
            :key="idx"
            class="docs__main-block"
            :id="`section-${idx}`"
            ref="sectionsRef"
        >
          <h2 class="docs__main-title" v-if="el.title">{{ t(`docs.policy.content[${idx}].title`) }}</h2>
          <i18n-t
              class="docs__main-text"
              v-if="el.text"
              scope="global"
              tag="p"
              :keypath="`docs.policy.content[${idx}].text`"
          >
            <template #domain>
              <router-link :to="translations.i18nRoute({ name: 'home' })">Findprotemplate.com</router-link>
            </template>
            <template #email>
              <a :href="'mailto:' + companyEmail" class="rules__link">{{ companyEmail }}</a>
            </template>
            <template #companyName>
              <span>{{ companyName }}</span>
            </template>
            <template #companyAddress>
              <span>{{ companyAddress }}</span>
            </template>
            <template #companyNumber>
              <span>{{ companyNumber }}</span>
            </template>
          </i18n-t>
          <div
              v-if="el.items"
              class="docs__main-inner"
              v-for="(itemEl, itemIdx) of el.items"
              :key="itemIdx"
          >
            <h6>{{ t(`docs.policy.content[${idx}].items[${itemIdx}].title`) }}</h6>
            <p class="docs__main-text">{{ t(`docs.policy.content[${idx}].items[${itemIdx}].text`) }}</p>
          </div>
          <ul class="docs__ul" v-if="el.list">
            <li
                v-for="(listEl, listIdx) of el.list"
                :key="listIdx"
                class="docs__main-text"
            >
              {{ t(`docs.policy.content[${idx}].list[${listIdx}].text`) }}
            </li>
          </ul>
          <p class="docs__main-text" v-if="el.text2">{{ t(`docs.policy.content[${idx}].text2`) }}</p>
          <ul class="docs__ul" v-if="el.list2">
            <li
                v-for="(listEl, listIdx) of el.list2"
                :key="listIdx"
                class="docs__main-text"
            >
              {{ t(`docs.policy.content[${idx}].list2[${listIdx}].text`) }}
            </li>
          </ul>
        </div>
      </el-scrollbar>
    </div>
  </div>
</template>

<script setup>
import UiTitle from "@/components/ui/ui-title.vue";
import {useI18n} from "vue-i18n";
import DocsNavigation from "@/components/docs/docs-navigation.vue";
import en from '@/locales/en-gb.json'
import {computed, nextTick, onMounted, onUnmounted, ref} from "vue";
import translations from "@/core/helpers/translations.js";
import {useCompanyStore} from "@/stores/company.js";

const {t} = useI18n()
const companyStore = useCompanyStore()

const policy = ref(en.docs.policy.content)

const company = computed(() => {
  return companyStore.companyInfo ? companyStore.companyInfo : null
})
const companyAddress = computed(() => {
  return company.value?.companyAddress || '';
});
const companyName = computed(() => {
  if (!company.value?.companyName) return '';
  return company.value?.companyName.split('|')[0].trim();
});
const companyNumber = computed(() => {
  if (!company.value?.companyName) return '';
  return company.value?.companyName.split('|')[1].trim();
});
const companyEmail = computed(() => {
  return company.value?.emailFrom || '';
});

const navRef = ref(null);
const scrollbarRef = ref(null);
const sectionsRef = ref([]);

const navigationList = [
  {
    id: 0,
    text: 'docs.policy.nav.item1'
  },
  {
    id: 1,
    text: 'docs.policy.nav.item2'
  },
  {
    id: 2,
    text: 'docs.policy.nav.item3'
  },
  {
    id: 3,
    text: 'docs.policy.nav.item4'
  },
  {
    id: 4,
    text: 'docs.policy.nav.item5'
  },
  {
    id: 5,
    text: 'docs.policy.nav.item6'
  },
  {
    id: 6,
    text: 'docs.policy.nav.item7'
  },
  {
    id: 7,
    text: 'docs.policy.nav.item8'
  },
  {
    id: 8,
    text: 'docs.policy.nav.item9'
  },
  {
    id: 9,
    text: 'docs.policy.nav.item10'
  }
]

const activeSection = ref(null);

const updateSizes = () => {
  nextTick(() => {
    if (navRef.value && scrollbarRef.value) {
      const navHeight = navRef.value.$el.offsetHeight;
      scrollbarRef.value.$el.style.height = `${navHeight}px`;
    }
  });
};

const handleScroll = () => {
  if (!scrollbarRef.value) return;

  const wrapElement = scrollbarRef.value.$el.querySelector('.el-scrollbar__wrap');
  if (!wrapElement) return;

  const scrollTop = wrapElement.scrollTop;
  const scrollPosition = scrollTop + 50;

  if (!sectionsRef.value.length) return;

  for (let i = 0; i < sectionsRef.value.length; i++) {
    const section = sectionsRef.value[i];
    if (!section) continue;

    const top = section.offsetTop;
    const height = section.offsetHeight;

    if (scrollPosition >= top && scrollPosition < top + height) {
      activeSection.value = i;
      break;
    }
  }
};

const scrollToSection = (id) => {
  if (!scrollbarRef.value || !sectionsRef.value[id]) return;

  const targetTop = sectionsRef.value[id].offsetTop;

  const wrapElement = scrollbarRef.value.$el.querySelector('.el-scrollbar__wrap');
  if (!wrapElement) return;

  wrapElement.scrollTo({
    top: targetTop - 30,
    behavior: 'smooth'
  });

  activeSection.value = id;
};

onMounted(() => {
  updateSizes();
  window.addEventListener('resize', updateSizes);

  nextTick(() => {
    handleScroll();
  });
});

onUnmounted(() => {
  window.removeEventListener('resize', updateSizes);
});
</script>

<style lang="scss" scoped>
.docs {
  top: -103px;
  margin-bottom: -110px;

  &__wrapper {
    display: flex;
    align-items: flex-start;
  }

  &__main {
    width: 100%;
    background-color: $white;
    padding: 30px 40px;
    position: relative;
    overflow-y: auto;

    &-block {
      margin-bottom: 30px;
      display: flex;
      flex-direction: column;
      gap: 10px;
    }

    &-text {
      color: $black;
      font-family: Syne, sans-serif;
      font-size: 16px;
      font-weight: 700;

      & a {
        transition: 0.2s;
        color: #49123f;

        &:hover {
          color: #772668;
        }
      }
    }

    &-title {
      font-family: Syne, sans-serif;
      font-size: 30px;
      font-weight: 700;
      background: linear-gradient(90deg, #FF85FD 0.5%, #7D045A 44.83%, #3D2B44 100%);
      background-clip: text;
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
    }

    &-inner {
      margin-top: 15px;
      display: flex;
      flex-direction: column;
      gap: 10px;

      & h6 {
        padding-left: 25px;
        color: $black;
        font-family: Syne, sans-serif;
        font-size: 22px;
        font-weight: 700;
      }
    }
  }

  &__ul {
    padding-left: 25px;
    display: flex;
    flex-direction: column;
    gap: 5px;
  }
}

@media (max-width: 800px) {
  .docs__main {
    height: auto !important;
  }
}
</style>